import $ from 'jquery'
import Chaplin from 'everlane-chaplin'
import Event from 'base/event'
import Hub from 'base/hub'
import template from 'base/lib/template'
import routes from 'desktop/routes'
import 'desktop/run_once'
import Layout from 'desktop/views/application/layout'
import { isInternal } from 'base/lib/route_to'

const controllers = {
  checkout: import('desktop/controllers/checkout_controller'),
  collections: import('desktop/controllers/collections_controller'),
  controller: import('desktop/controllers/controller'),
  factories: import('desktop/controllers/factories_controller'),
  giftcards: import('desktop/controllers/giftcards_controller'),
  home: import('desktop/controllers/home_controller'),
  not_found: import('base/controllers/not_found_controller'),
  oauth: import('desktop/controllers/oauth_controller'),
  pages: import('desktop/controllers/pages_controller'),
  password_resets: import('base/controllers/password_resets_controller'),
  products: import('desktop/controllers/products_controller'),
  referrals: import('desktop/controllers/referrals_controller'),
  search: import('base/controllers/search_controller'),
  sitemap: import('desktop/controllers/sitemap_controller'),
}

const titleTemplate = template('<@= subtitle @>')

export default class Application extends Chaplin.Application {
  initDispatcher(...initArgs) {
    super.initDispatcher(...initArgs)

    const originalExecuteAction = this.dispatcher.executeAction
    this.dispatcher.executeAction = function (controller, route, ...executeArgs) {
      Hub.pub(Event.App.TRANSITION_START, `${route.controller}#${route.action}`)
      return originalExecuteAction.call(this, controller, route, ...executeArgs)
    }

    this.dispatcher.loadController = (name, handler) => {
      Promise.resolve(controllers[name]).then(module => {
        handler(module.default)
      })
    }
  }

  initRouter() {
    const RouterProto = Chaplin.Router.prototype
    RouterProto.oldRoute = RouterProto.route
    const application = this
    RouterProto.route = function (pathDesc, params, options) {
      if (!application.dispatcher.currentRoute) {
        $('#content').empty()
      }

      try {
        if (pathDesc?.url?.includes('#')) {
          window.location = pathDesc.url
        } else {
          const url = new URL(pathDesc?.url ?? '', window.location.origin)

          if (isInternal(url.pathname)) {
            // Ensure that url is relative path
            const relativePath = { url: url.href.replace(url.origin, '') }
            this.oldRoute(relativePath, params, options)
          } else {
            window.location = url.href
          }
        }
      } catch (error) {
        if (error.message !== 'Router#route: request was not routed') {
          throw error
        }
        window.location = pathDesc.url
      }
    }

    return super.initRouter(routes, { hashChange: false })
  }

  // Override standard layout initializer
  // ------------------------------------
  initLayout() {
    const LayoutProto = Chaplin.Layout.prototype
    LayoutProto.oldOpenLink = LayoutProto.openLink
    LayoutProto.openLink = function (event) {
      if (event.isDefaultPrevented()) {
        return
      }

      const el = $ ? event.currentTarget : event.delegateTarget

      setTimeout(() => this.oldOpenLink(event))

      const isNewTab = event.ctrlKey || event.shiftKey || event.metaKey
      const isAnchorLink = el.getAttribute('href')?.startsWith?.('#')

      if (!this.isExternalLink(el) && !isNewTab && !isAnchorLink) {
        event.preventDefault()
      }
    }

    this.layout = new Layout({
      title: this.title,
      titleTemplate,
    })
  }

  // Instantiate common controllers
  // ------------------------------
  // initControllers: ->
  // These controllers are active during the whole application runtime.
  // You don’t need to instantiate all controllers here, only special
  // controllers which do not to respond to routes. They may govern models
  // and views which are needed the whole time, for example header, footer
  // or navigation views.
  // This must be called in @initDispatcher
  initControllers(...args) {
    return super.initControllers(...args)
  }

  // Create additional mediator properties
  // -------------------------------------
  initMediator(...args) {
    return super.initMediator(...args)
  }

  start(...args) {
    Chaplin.mediator.subscribe('dispatcher:dispatch', (controller, params, route) => {
      const previousRoute = route.previous ? `/${route.previous.path}` : null

      Hub.pub(Event.App.ROUTE, {
        path: `/${route.path}`,
        previousRoute,
      })
    })

    return super.start(...args)
  }
}
